import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Select, Button, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/ApiService';
import '../Quiz/quizMain.css';

function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, value);
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
}

const PRIZE_TYPE_LABELS = {
    beko: 'Beko',
    dtl: 'DTL',
    main_clio: 'Clio',
};

const PRIZE_TYPE_ORDER = {
    beko: 0,
    dtl: 1,
    main_clio: 2,
};

const DRAW_TYPE_ORDER = {
    weekly: 0,
    main: 1,
};

const WINNERS_FETCH_LIMIT = 500;

const RECORD_TYPE_LABELS = {
    all: 'Svi',
    regular: 'Regularni',
    reserve: 'Rezervni',
};

const getDrawRoundGroupKey = (winner) => winner?.drawRoundId || winner?.drawRound?.id || 'no-round';

const getPrizeTypeOrder = (prizeType) => PRIZE_TYPE_ORDER[prizeType] ?? Number.MAX_SAFE_INTEGER;

const getDrawTypeOrder = (type) => DRAW_TYPE_ORDER[type] ?? Number.MAX_SAFE_INTEGER;

const getRoundDateValue = (winner) => {
    const drawAt = winner?.drawRound?.drawAt;
    if (!drawAt) return Number.MAX_SAFE_INTEGER;
    const value = dayjs(drawAt).valueOf();
    return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const orderWinnersForDisplay = (winners) => {
    return [...winners].sort((a, b) => {
        const drawAtDiff = getRoundDateValue(a) - getRoundDateValue(b);
        if (drawAtDiff !== 0) return drawAtDiff;

        const drawTypeDiff = getDrawTypeOrder(a?.drawRound?.type) - getDrawTypeOrder(b?.drawRound?.type);
        if (drawTypeDiff !== 0) return drawTypeDiff;

        const roundNumberDiff = (a?.drawRound?.roundNumber ?? Number.MAX_SAFE_INTEGER) -
            (b?.drawRound?.roundNumber ?? Number.MAX_SAFE_INTEGER);
        if (roundNumberDiff !== 0) return roundNumberDiff;

        const prizeTypeDiff = getPrizeTypeOrder(a?.prizeType) - getPrizeTypeOrder(b?.prizeType);
        if (prizeTypeDiff !== 0) return prizeTypeDiff;

        const slotIndexDiff = (a?.slotIndex ?? Number.MAX_SAFE_INTEGER) - (b?.slotIndex ?? Number.MAX_SAFE_INTEGER);
        if (slotIndexDiff !== 0) return slotIndexDiff;

        const reserveDiff = Number(a?.isReserve) - Number(b?.isReserve);
        if (reserveDiff !== 0) return reserveDiff;

        return Number(a?.isPromoted) - Number(b?.isPromoted);
    });
};

const getDrawRoundSectionLabel = (round) => {
    if (!round) return 'Bez kruga';
    if (round.type === 'main') return 'Glavno izvlacenje';
    if (round.type === 'weekly') return `Nedeljni #${round.roundNumber || '-'}`;
    if (round.roundNumber) return `${round.type || 'Krug'} #${round.roundNumber}`;
    return round.type || 'Krug';
};

const getWinnerTypeLabel = (record) => {
    if (!record?.isReserve) return 'Regular';
    if (record.isPromoted) return 'Promovisan';
    return 'Rezerva';
};

const PromotionWinners = () => {
    const navigate = useNavigate();
    const [winners, setWinners] = useState([]);
    const [drawRounds, setDrawRounds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [promotingId, setPromotingId] = useState(null);
    const [confirmWinner, setConfirmWinner] = useState(null);
    const [filters, setFilters] = useState({
        drawRoundId: undefined,
        prizeType: undefined,
        recordType: 'all',
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);

    const fetchDrawRounds = useCallback(async () => {
        const [success, error] = await apiService.get('promotion/draw-rounds');

        if (error) return;

        setDrawRounds(success?.data?.drawRounds || []);
    }, []);

    const fetchWinners = useCallback(async (currentFilters) => {
        setLoading(true);

        const queryParams = {
            page: 1,
            limit: WINNERS_FETCH_LIMIT,
            drawRoundId: currentFilters.drawRoundId,
            prizeType: currentFilters.prizeType,
        };

        if (currentFilters.recordType === 'regular') {
            queryParams.isReserve = 'false';
        } else if (currentFilters.recordType === 'reserve') {
            queryParams.isReserve = 'true';
        }

        const query = buildQuery(queryParams);
        const [success, error] = await apiService.get(`promotion/winners${query}`);

        setLoading(false);

        if (error) {
            toast.error('Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        const data = success?.data;
        setWinners(orderWinnersForDisplay(data?.winners || []));
    }, []);

    useEffect(() => {
        fetchDrawRounds();
    }, [fetchDrawRounds]);

    useEffect(() => {
        fetchWinners(appliedFilters);
    }, [appliedFilters, fetchWinners]);

    const winnersByDrawRound = useMemo(() => {
        const grouped = [];
        const groupedMap = new Map();

        winners.forEach((winner) => {
            const groupKey = getDrawRoundGroupKey(winner);

            if (!groupedMap.has(groupKey)) {
                const group = {
                    key: groupKey,
                    label: getDrawRoundSectionLabel(winner.drawRound),
                    winners: [],
                };
                groupedMap.set(groupKey, group);
                grouped.push(group);
            }

            groupedMap.get(groupKey).winners.push(winner);
        });

        return grouped;
    }, [winners]);

    const formatPhaseLabel = (round) => {
        if (!round) return '-';
        const drawAtLabel = round.drawAt ? dayjs(round.drawAt).format('D MMM YYYY') : '-';
        return `${getDrawRoundSectionLabel(round)} (${drawAtLabel})`;
    };

    const handlePromote = async () => {
        if (!confirmWinner) return;

        setPromotingId(confirmWinner.id);

        const [success, error] = await apiService.patch(
            `promotion/winners/${confirmWinner.id}/promote`
        );

        setPromotingId(null);
        setConfirmWinner(null);

        if (error) {
            const message = error?.response?.data?.error?.message;
            toast.error(message || 'Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        toast.success('Rezervni dobitnik je promovisan');
        if (success?.data?.winner) {
            setWinners((prev) =>
                orderWinnersForDisplay(
                    prev.map((w) => (w.id === success.data.winner.id ? success.data.winner : w))
                )
            );
        } else {
            fetchWinners(appliedFilters);
        }
    };

    const canPromote = (record) =>
        record.isReserve === true &&
        record.drawRound?.status === 'completed' &&
        record.isPromoted !== true;

    const getWinnerEntryId = (record) =>
        record.entryId ||
        record.entry?.id ||
        record.entry?.entryId ||
        record.entry?.entry?.id ||
        record.promotionEntryId ||
        record.promotionEntry?.id ||
        record.receiptEntryId;

    const resolveEntryIdByPfr = async (record) => {
        if (!record?.pfr) return null;

        const query = buildQuery({
            page: 1,
            limit: 1,
            pfr: record.pfr,
        });

        const [success, error] = await apiService.get(`promotion/entries${query}`);
        if (error) return null;

        return success?.data?.entries?.[0]?.id || null;
    };

    const openEntryDetails = async (record) => {
        let entryId = getWinnerEntryId(record);
        if (!entryId) {
            entryId = await resolveEntryIdByPfr(record);
        }

        if (!entryId) {
            toast.error('Prijava za ovog dobitnika nije pronadjena');
            return;
        }

        navigate(`/promotion/entries/${entryId}`);
    };

    const applyFilters = () => {
        setAppliedFilters({ ...filters });
    };

    const columns = [
        {
            title: 'PFR',
            dataIndex: 'pfr',
            key: 'pfr',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text) => text || '-',
        },
        {
            title: 'Telefon',
            dataIndex: 'phone',
            key: 'phone',
            render: (text) => text || '-',
        },
        {
            title: 'Nagrada',
            dataIndex: 'prizeType',
            key: 'prizeType',
            render: (type) => PRIZE_TYPE_LABELS[type] || type,
        },
        {
            title: 'Slot',
            dataIndex: 'slotIndex',
            key: 'slotIndex',
            render: (value) => value ?? '-',
        },
        {
            title: 'Faza',
            key: 'drawRound',
            render: (_, record) => formatPhaseLabel(record.drawRound),
        },
        {
            title: 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => (text ? dayjs(text).format('D MMM YYYY HH:mm') : '-'),
        },
        {
            title: 'Tip zapisa',
            key: 'recordType',
            render: (_, record) => (
                <Tag
                    color={
                        record.isPromoted ? 'blue' : record.isReserve ? 'gold' : 'green'
                    }
                >
                    {getWinnerTypeLabel(record)}
                </Tag>
            ),
        },
        {
            title: 'Akcija',
            key: 'action',
            render: (_, record) =>
                canPromote(record) ? (
                    <Button
                        type="primary"
                        size="small"
                        loading={promotingId === record.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            setConfirmWinner(record);
                        }}
                    >
                        Promovisi
                    </Button>
                ) : null,
        },
    ];

    return (
        <div className="quizMain">
            <div className="title">NI dobitnici</div>

            <div className="filters">
                <div className="holder">
                    <label htmlFor="drawRoundFilter">Faza</label>
                    <Select
                        id="drawRoundFilter"
                        allowClear
                        placeholder="Sve faze"
                        style={{ width: 280 }}
                        value={filters.drawRoundId}
                        onChange={(value) => setFilters((prev) => ({ ...prev, drawRoundId: value }))}
                        options={drawRounds.map((round) => ({
                            value: round.id,
                            label: formatPhaseLabel(round),
                        }))}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="prizeTypeFilter">Tip nagrade</label>
                    <Select
                        id="prizeTypeFilter"
                        allowClear
                        placeholder="Sve nagrade"
                        style={{ width: 160 }}
                        value={filters.prizeType}
                        onChange={(value) => setFilters((prev) => ({ ...prev, prizeType: value }))}
                        options={Object.entries(PRIZE_TYPE_LABELS).map(([value, label]) => ({
                            value,
                            label,
                        }))}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="recordTypeFilter">Tip zapisa</label>
                    <Select
                        id="recordTypeFilter"
                        style={{ width: 160 }}
                        value={filters.recordType}
                        onChange={(value) => setFilters((prev) => ({ ...prev, recordType: value }))}
                        options={Object.entries(RECORD_TYPE_LABELS).map(([value, label]) => ({
                            value,
                            label,
                        }))}
                    />
                </div>

                <div className="holder">
                    <label>&nbsp;</label>
                    <Button type="primary" onClick={applyFilters}>
                        Primeni
                    </Button>
                </div>
            </div>

            {winnersByDrawRound.length === 0 ? (
                <Table
                    rowKey="id"
                    dataSource={[]}
                    columns={columns}
                    bordered
                    loading={loading}
                    pagination={false}
                />
            ) : (
                winnersByDrawRound.map((group) => (
                    <div key={group.key} style={{ marginTop: 16 }}>
                        <h3>{group.label}</h3>
                        <Table
                            rowKey="id"
                            dataSource={group.winners}
                            columns={columns}
                            bordered
                            loading={loading}
                            rowClassName={(record) => {
                                if (record.isPromoted) return 'promotion-promoted-row';
                                return record.isReserve ? 'promotion-reserve-row' : 'promotion-winner-row';
                            }}
                            onRow={(record) => {
                                const entryId = getWinnerEntryId(record);
                                return {
                                    onClick: () => openEntryDetails(record),
                                    style: { cursor: entryId ? 'pointer' : 'default' },
                                };
                            }}
                            pagination={false}
                        />
                    </div>
                ))
            )}

            <Modal
                title="Potvrda promocije"
                open={!!confirmWinner}
                onOk={handlePromote}
                onCancel={() => setConfirmWinner(null)}
                okText="Promovisi"
                cancelText="Otkazi"
                confirmLoading={!!promotingId}
            >
                {confirmWinner && (
                    <>
                        Da li ste sigurni da zelite da promovisete rezervnog dobitnika{' '}
                        <strong>{confirmWinner.pfr}</strong>?
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PromotionWinners;
